import React from 'react';
import { normalizeAgentMessage } from 'services/agentMessageContract';
import { useSelector } from 'react-redux';
import { Box, Card, CardContent, Typography, Chip, IconButton, Dialog, Tooltip, Fade } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { DownloadOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import trustedInlinePluginFactory from './ChatMessage/trustedInlinePlugin';
import ResearchToolBox from 'components/chat/ResearchToolBox';
import { exportSingleMessageDocx } from 'utils/exportChatDocx';
import MarkdownErrorBoundary from './MarkdownErrorBoundary';
import { stabilizeMarkdownForStreaming, normalizeInlineGfmTables, maskIncompleteGfmTables } from '../utils/markdown';
import { rateChatMessage } from 'services/agentService';
import InsufficientCreditsFreeCard from './InsufficientCreditsFreeCard';
import InsufficientCreditsPaidCard from './InsufficientCreditsPaidCard';
import MarketOverview from 'sections/lp-analysis/Market/MarketOverviewResolver';
import MarketTrends from 'sections/lp-analysis/MarketTrends';
import GetWhoToBuyReportViewer from 'components/chat/GetWhoToBuyReportViewer';
import { useDispatch } from 'react-redux';
import { openModal } from 'store/reducers/actions';
import { Button, Stack } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import CampaignIcon from '@mui/icons-material/Campaign';

/**
 * Helper function to parse tool_result if it's a string containing JSON
 * Handles both plain JSON strings and JSON wrapped in markdown code blocks
 */
const parseToolResult = (toolResult) => {
  if (!toolResult) return toolResult;

  // If it's already an object, return as-is
  if (typeof toolResult === 'object' && !Array.isArray(toolResult)) {
    return toolResult;
  }

  // If it's a string, try to parse it
  if (typeof toolResult === 'string') {
    try {
      // Remove markdown code fences if present (```json ... ```)
      let jsonString = toolResult.trim();
      if (jsonString.startsWith('```')) {
        // Remove opening ```json or ```
        jsonString = jsonString.replace(/^```(?:json)?\s*\n?/, '');
        // Remove closing ```
        jsonString = jsonString.replace(/\n?```\s*$/, '');
      }
      // Parse the JSON string
      return JSON.parse(jsonString);
    } catch (e) {
      console.error('Failed to parse tool_result string as JSON:', e);
      // If parsing fails, return the original string
      return toolResult;
    }
  }

  return toolResult;
};

const ChatMessage = ({
  message,
  theme,
  onOpenReport,
  allMessages = [],
  readOnly = false,
  liveToolStates = null,
  compactLayout = false,
  onFollowUps,
  onClickFollowUp,
  isFullscreen = false
}) => {
  const RatingControls = ({ message }) => {
    const [pending, setPending] = React.useState(false);
    const [rated, setRated] = React.useState(null); // null | true | false
    const [hidden, setHidden] = React.useState(false);

    if (!message || !message.backendMessageId || message.streaming) return null;

    const submit = async (isHelpful) => {
      if (!message.backendMessageId) return;
      const previous = rated;
      setPending(true);
      setRated(isHelpful);
      try {
        await rateChatMessage(message.backendMessageId, isHelpful);
      } catch (e) {
        if (e && e.status === 403) setHidden(true);
        setRated(previous);
      } finally {
        setPending(false);
      }
    };

    if (hidden) return null;

    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', mr: 0.5 }}>
        <Tooltip title="Helpful">
          <span>
            <IconButton size="small" color={rated === true ? 'success' : 'default'} disabled={pending} onClick={() => submit(true)}>
              <ThumbUpAltOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Not helpful">
          <span>
            <IconButton size="small" color={rated === false ? 'error' : 'default'} disabled={pending} onClick={() => submit(false)}>
              <ThumbDownAltOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  const FooterControls = ({ message, content }) => {
    const [copied, setCopied] = React.useState(false);
    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(normalizeInlineGfmTables(content || ''));
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      } catch (_) {
        // ignore
      }
    };
    return (
      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Fade in={copied} timeout={{ enter: 200, exit: 300 }}>
          <Typography variant="caption" sx={{ color: 'success.main', ml: 1 }}>
            Copied
          </Typography>
        </Fade>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {message.backendMessageId ? <RatingControls message={message} /> : null}
          {String(content || '').trim().length > 0 && (
            <>
              <Tooltip title="Copy message">
                <IconButton size="small" onClick={handleCopy}>
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download .docx">
                <IconButton
                  size="small"
                  onClick={() => {
                    const ts = message.timestamp ? new Date(message.timestamp) : new Date();
                    const stamp = isNaN(ts.getTime()) ? Date.now() : ts.getTime();
                    exportSingleMessageDocx(message, `assistant-message-${stamp}.docx`);
                  }}
                >
                  <DownloadOutlined />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>
    );
  };
  //   const currentTaskState = useSelector((state) => state.currentTaskReducer);
  const dispatch = useDispatch();
  const globalHistoryResult = useSelector((state) => state.historyReducer.historyResult);
  const plan = useSelector((state) => state.proAccountReducer.plan);
  const trialWasActivated = useSelector((state) => state.proAccountReducer.trial_was_activated);
  const userCountry = useSelector((state) => state.proAccountReducer.country);
  //   const openTaskId = currentTaskState?.task_id || null;
  // (no top-level hooks needed for insufficient credits cards)
  const normalizedMessage = normalizeAgentMessage(message);
  const parsedContent = normalizedMessage.metadata || null;
  const displayContent = normalizedMessage.content || '';
  let toolCalls = parsedContent?.tool_calls || null;

  // Fallback: infer tool calls from surrounding tool messages if assistant didn't include tool_calls
  // IMPORTANT: do NOT infer while the current message is streaming, otherwise a previous
  // run's image/result can flash before the new tool actually starts.
  if (
    !message.streaming &&
    (!toolCalls || toolCalls.length === 0) &&
    Array.isArray(allMessages) &&
    allMessages.length > 0 &&
    message.type !== 'user'
  ) {
    try {
      const currentTs = Number(message.timestamp) || 0;
      const toMillis = (v) => {
        try {
          return new Date(v).getTime();
        } catch (e) {
          return 0;
        }
      };
      // Find the most recent non-tool message before this assistant message
      let prevBoundary = 0;
      for (const m of allMessages) {
        if (m.role === 'tool') continue;
        const ts = toMillis(m.created_at || m.timestamp);
        if (ts < currentTs && ts > prevBoundary) prevBoundary = ts;
      }
      const inferred = allMessages
        .filter((m) => m.role === 'tool' && (m.metadata?.tool_name || m.tool_name))
        .filter((m) => {
          const ts = toMillis(m.created_at || m.timestamp);
          return ts > prevBoundary && ts <= currentTs;
        })
        .map((m) => ({
          tool: m.metadata?.tool_name || m.tool_name,
          call_id: m.metadata?.tool_call_id,
          args: m.metadata?.args || m.args || {},
          tool_result: typeof m.metadata?.tool_result !== 'undefined' ? m.metadata.tool_result : m.tool_result || null
        }));
      if (inferred.length > 0) {
        toolCalls = inferred;
      }
    } catch (e) {
      // ignore
    }
  }

  // Extract server-provided reasoning text (if present)
  const reasonText = (() => {
    try {
      if (parsedContent) {
        const t = parsedContent.reasoning_summary || parsedContent.reasoning_text || parsedContent.reasoning;
        if (typeof t === 'string' && t.trim().length > 0) return t;
      }
    } catch (e) {
      // ignore
    }
    return '';
  })();

  const followUpQuestions = Array.isArray(parsedContent?.follow_up_actions) ? parsedContent.follow_up_actions : [];

  React.useEffect(() => {
    try {
      if (typeof onFollowUps === 'function' && Array.isArray(followUpQuestions) && followUpQuestions.length > 0) {
        onFollowUps(followUpQuestions);
      }
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(followUpQuestions)]);

  // Collapsible inline reasoning block (must be a component to use hooks)
  const ReasoningInline = ({ text }) => {
    const [open, setOpen] = React.useState(false);
    if (!text || typeof text !== 'string' || text.trim().length === 0) return null;
    return (
      <Card sx={{ mb: 1.5, backgroundColor: theme.palette.grey[100] }}>
        <CardContent sx={{ p: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">
              Reasoning
            </Typography>
            <Chip size="small" label={open ? 'Hide' : 'Show'} onClick={() => setOpen((v) => !v)} />
          </Box>
          {open && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
              {text}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  // Inline streaming indicator helpers
  const isStreaming = !!message.streaming;
  const showInlineThinking = isStreaming && (!displayContent || String(displayContent).trim().length === 0);
  const StreamingDots = () => (
    <Box component="span" sx={{ ml: 0.5, display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
      <Box
        component="span"
        sx={{
          width: 4,
          height: 4,
          borderRadius: '50%',
          bgcolor: theme.palette.text.secondary,
          opacity: 0.6,
          animation: 'blink 1.2s infinite'
        }}
      />
      <Box
        component="span"
        sx={{
          width: 4,
          height: 4,
          borderRadius: '50%',
          bgcolor: theme.palette.text.secondary,
          opacity: 0.6,
          animation: 'blink 1.2s 0.2s infinite'
        }}
      />
      <Box
        component="span"
        sx={{
          width: 4,
          height: 4,
          borderRadius: '50%',
          bgcolor: theme.palette.text.secondary,
          opacity: 0.6,
          animation: 'blink 1.2s 0.4s infinite'
        }}
      />
      <style>{`@keyframes blink { 0%{opacity:.2} 50%{opacity:1} 100%{opacity:.2} }`}</style>
    </Box>
  );

  // Detect and normalize image messages (data URI or local attachments)
  const isDataImageUri = (text) => {
    try {
      return typeof text === 'string' && /^data:image\/[a-zA-Z0-9.+-]+;base64,/.test(text.trim());
    } catch {
      return false;
    }
  };

  const getImageSourcesForMessage = () => {
    const sources = [];
    // From attachments on the local message object
    if (Array.isArray(message.attachments)) {
      for (const att of message.attachments) {
        try {
          if (att && att.base64 && typeof att.type === 'string' && att.type.startsWith('image/')) {
            sources.push(`data:${att.type};base64,${att.base64}`);
          } else if (typeof att === 'string' && isDataImageUri(att)) {
            sources.push(att);
          }
        } catch {
          // ignore attachment parsing errors
        }
      }
    }
    // If the message content itself is a data:image URI
    if (typeof message.content === 'string' && isDataImageUri(message.content)) {
      sources.push(message.content.trim());
    } else if (message.type !== 'user' && typeof displayContent === 'string' && isDataImageUri(displayContent)) {
      // Also support assistant messages that may contain a pure data URI
      sources.push(displayContent.trim());
    }
    return sources;
  };

  const imageSources = getImageSourcesForMessage();
  const [previewSrc, setPreviewSrc] = React.useState(null);

  // Normalize tool names across sources (tool_calls, live entries, tool messages)
  const normalizeToolName = (name) =>
    String(name || '')
      .replace('functions.', '')
      .replace(/[<>]/g, '')
      .trim()
      .toLowerCase();

  // Find artifacts for each tool call by looking for corresponding tool messages
  const getArtifactsForToolCall = (toolCall) => {
    if (!allMessages || !toolCall) return { artifacts: [], toolResult: null };
    // If this toolCall already carries its own result (e.g., inferred from history), trust it
    if (typeof toolCall.tool_result !== 'undefined' && toolCall.tool_result !== null) {
      return { artifacts: [], toolResult: toolCall.tool_result };
    }
    const targetName = normalizeToolName(toolCall.tool);
    if (toolCall.call_id) {
      const exact = allMessages.find((item) => item.role === 'tool' && item.metadata?.tool_call_id === toolCall.call_id);
      if (exact) return { artifacts: exact.artifacts || [], toolResult: exact.metadata?.tool_result ?? null };
    }
    // If the same tool is currently running (live, not completed), avoid backfilling from history
    try {
      if (liveToolStates && Array.isArray(liveToolStates.order)) {
        for (const id of liveToolStates.order) {
          const entry = liveToolStates.map?.[id];
          if (!entry) continue;
          const ename = normalizeToolName(entry.tool_name || '');
          if (ename === targetName && entry.status === 'running') {
            return { artifacts: [], toolResult: null };
          }
        }
      }
    } catch (e) {
      // ignore
    }
    const toMillis = (v) => {
      try {
        return new Date(v).getTime();
      } catch (e) {
        return 0;
      }
    };
    const assistantTs = Number(message.timestamp) || 0;

    // Collect all matching tool messages
    const candidates = allMessages.filter(
      (msg) => msg.role === 'tool' && normalizeToolName(msg.metadata?.tool_name || msg.tool_name) === targetName
    );
    if (candidates.length === 0) return { artifacts: [], toolResult: null };

    // Prefer the most recent tool message at or before this assistant message
    let best = null;
    let bestTs = -1;
    for (const m of candidates) {
      const ts = toMillis(m.created_at || m.timestamp);
      if ((ts <= assistantTs || assistantTs === 0) && ts >= bestTs) {
        best = m;
        bestTs = ts;
      }
    }
    // If none are before the assistant, fallback to the last candidate
    if (!best) best = candidates[candidates.length - 1];

    // Prefer a candidate that has a usable image/url payload over an error/string
    const hasUsablePayload = (res) =>
      !!(res && typeof res === 'object' && (res.b64_image || res.url || res.result?.b64_image || res.result?.url));
    let bestRes = typeof best?.metadata?.tool_result !== 'undefined' ? best.metadata.tool_result : best?.tool_result;
    if (!hasUsablePayload(bestRes)) {
      const successFallback = [...candidates]
        .reverse()
        .find((m) => hasUsablePayload(typeof m.metadata?.tool_result !== 'undefined' ? m.metadata.tool_result : m.tool_result));
      if (successFallback) {
        best = successFallback;
        bestRes = typeof best?.metadata?.tool_result !== 'undefined' ? best.metadata.tool_result : best?.tool_result;
      }
    }

    return {
      artifacts: best?.artifacts || [],
      toolResult: bestRes || null
    };
  };

  // Build combined list of tool calls including live streaming tools (for loader)
  let combinedToolCalls = toolCalls && Array.isArray(toolCalls) ? [...toolCalls] : [];
  const liveEntriesRaw =
    liveToolStates && Array.isArray(liveToolStates.order) ? liveToolStates.order.map((id) => ({ id, ...liveToolStates.map[id] })) : [];

  // Determine message time window to bind live tool cards to the correct turn only
  const assistantTs = Number(message.timestamp) || 0;
  const toMillisLocal = (v) => {
    try {
      return new Date(v).getTime();
    } catch (e) {
      return 0;
    }
  };
  let prevBoundaryTs = 0;
  if (Array.isArray(allMessages) && allMessages.length > 0) {
    for (const m of allMessages) {
      if (m.role === 'tool') continue;
      const ts = toMillisLocal(m.created_at || m.timestamp);
      if (ts < assistantTs && ts > prevBoundaryTs) prevBoundaryTs = ts;
    }
  }

  // Also compute the next non-tool message boundary after this assistant message (if any)
  let nextBoundaryTsLocal = Number.MAX_SAFE_INTEGER;
  if (Array.isArray(allMessages) && allMessages.length > 0) {
    for (const m of allMessages) {
      if (m.role === 'tool') continue;
      const ts = toMillisLocal(m.created_at || m.timestamp);
      if (ts > assistantTs && ts < nextBoundaryTsLocal) nextBoundaryTsLocal = ts;
    }
  }

  // Filter out unknown/noise entries and keep the latest per tool name, only for the current turn
  const liveEntriesMap = new Map();
  for (const e of liveEntriesRaw) {
    const name = e && e.tool_name ? e.tool_name : '';
    if (!name || name === 'unknown' || name === 'unknown_tool') continue;
    const startedAt = typeof e.startTs === 'number' ? e.startTs : 0;
    // During streaming: show tools that started after the previous non-tool message (including completed ones).
    // After streaming: restrict to tools that started within the window of this turn (prevBoundaryTs .. nextBoundaryTsLocal).
    const withinWindow = message.streaming ? startedAt > prevBoundaryTs : startedAt > prevBoundaryTs && startedAt < nextBoundaryTsLocal;
    if (!withinWindow) continue;
    // overwrite to keep the most recent occurrence
    liveEntriesMap.set(e.call_id || e.id || name, e);
  }
  const liveEntries = Array.from(liveEntriesMap.values());
  if (liveEntries && liveEntries.length > 0) {
    const identity = (call) => call.call_id || `legacy:${normalizeToolName(call.tool)}`;
    const included = new Set(combinedToolCalls.map(identity));
    combinedToolCalls = combinedToolCalls.map((call) => {
      const live = liveEntries.find((entry) => call.call_id ? (entry.call_id || entry.id) === call.call_id : normalizeToolName(entry.tool_name) === normalizeToolName(call.tool));
      return live && live.tool_result !== undefined ? { ...call, tool_result: live.tool_result } : call;
    });
    for (const entry of liveEntries) {
      const call = { tool: entry.tool_name, call_id: entry.call_id || entry.id, tool_result: entry.tool_result ?? null };
      if (!included.has(identity(call))) {
        combinedToolCalls.push(call);
        included.add(identity(call));
      }
    }
  }

  // If we still have no calls but do have live entries (e.g., tool_output before assistant emits tool_calls),
  // render based on live entries directly so the toolbox appears during streaming.
  if ((!combinedToolCalls || combinedToolCalls.length === 0) && liveEntries && liveEntries.length > 0) {
    combinedToolCalls = liveEntries.map((e) => ({ tool: e.tool_name, call_id: e.call_id || e.id, tool_result: e.tool_result ?? null }));
  }

  // Special handling for web_search: don't render as a full ResearchToolBox card.
  // Instead, show a compact counter: "Completed N web searches" that updates during streaming
  // and persists after completion.
  const nonWebToolCalls = (combinedToolCalls || []).filter((c) => normalizeToolName(c.tool) !== 'web_search');

  // Determine the next non-tool message timestamp to bound this turn's window for finalized messages
  let nextBoundaryTs = Number.MAX_SAFE_INTEGER;
  if (Array.isArray(allMessages) && allMessages.length > 0) {
    for (const m of allMessages) {
      if (m.role === 'tool') continue;
      const ts = toMillisLocal(m.created_at || m.timestamp);
      if (ts > assistantTs && ts < nextBoundaryTs) nextBoundaryTs = ts;
    }
  }

  // Count web_search tool messages within this turn window (between previous and next non-tool messages)
  const webSearchHistoryCount = (() => {
    try {
      if (!Array.isArray(allMessages)) return 0;
      return allMessages
        .filter((m) => m.role === 'tool' && normalizeToolName(m.metadata?.tool_name || m.tool_name) === 'web_search')
        .filter((m) => {
          const ts = toMillisLocal(m.created_at || m.timestamp);
          return ts > prevBoundaryTs && ts < nextBoundaryTs;
        }).length;
    } catch {
      return 0;
    }
  })();

  // During streaming, treat each web_search start as instantly completed and count them.
  const liveWebCount = (() => {
    try {
      const order = liveToolStates && Array.isArray(liveToolStates.order) ? liveToolStates.order : [];
      const map = liveToolStates && liveToolStates.map ? liveToolStates.map : {};
      let count = 0;
      for (const id of order) {
        const entry = map[id];
        if (!entry || normalizeToolName(entry.tool_name) !== 'web_search') continue;
        const startedAt = typeof entry.startTs === 'number' ? entry.startTs : 0;
        const withinWindow = message.streaming || (startedAt > prevBoundaryTs && (assistantTs === 0 || startedAt <= assistantTs));
        if (withinWindow) count += 1;
      }
      return count;
    } catch {
      return 0;
    }
  })();
  const webSearchCompletedCount = message.streaming ? liveWebCount : Math.max(webSearchHistoryCount, liveWebCount);
  const hasWebSearchActivity = webSearchCompletedCount > 0;

  // (Removed) parseTrustedReportRefs – no longer needed since inline renderer handles both formats directly

  const toolDisplayName = (tool) => {
    const map = { landing_page_analysis: 'Landing Page Analysis' };
    return map[tool] || tool.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const buildExternalReportUrl = (tool, taskId, highlightText) => {
    if (tool === 'landing_page_analysis') return `/lp-analysis/${encodeURIComponent(taskId)}`;
    if (tool === 'compare_competitors') return `/competitors-history/${encodeURIComponent(taskId)}`;
    if (tool === 'reviews_analysis') {
      const q = highlightText ? `?highlight=${encodeURIComponent(highlightText)}` : '';
      return `/reviews/${encodeURIComponent(taskId)}${q}`;
    }
    return '#';
  };

  // Locate existing tool_result from chat history without refetching
  const getToolResultFromHistory = (tool, taskId) => {
    try {
      if (!Array.isArray(allMessages)) return null;
      const t = normalizeToolName(tool);
      const candidates = allMessages.filter(
        (m) =>
          m &&
          m.role === 'tool' &&
          normalizeToolName(m.metadata?.tool_name || m.tool_name) === t &&
          (m.metadata?.tool_result || m.tool_result)
      );
      if (candidates.length === 0) return null;
      // Prefer by matching task_id if present
      const byTask = candidates.find((m) => {
        try {
          const res = m.metadata?.tool_result || m.tool_result || {};
          const tid = res.task_id || res.overview_result?.task_id;
          return taskId && tid && String(tid) === String(taskId);
        } catch {
          return false;
        }
      });
      const chosen = byTask || candidates[candidates.length - 1];
      return chosen?.metadata?.tool_result || chosen?.tool_result || null;
    } catch {
      return null;
    }
  };

  // Open report and navigate/highlight by excerpt ONLY (no title fallback)
  const openTrustedReportToPath = async (tool, taskId, path, excerpt) => {
    try {
      // If the same report is already open, do not reopen
      //   console.log('openTrustedReportToPath', tool, taskId, path, excerpt)
      // Prefer in-memory tool_result, then global history, else open external route
      const existing = getToolResultFromHistory(tool, taskId);
      if (existing && typeof onOpenReport === 'function') {
        onOpenReport({ tool: `functions.${tool}`, tool_result: existing }, [], null);
      } else if (globalHistoryResult && String(globalHistoryResult.task_id || '') === String(taskId)) {
        if (typeof onOpenReport === 'function') {
          onOpenReport({ tool: `functions.${tool}`, tool_result: globalHistoryResult }, [], null);
        }
      } else {
        // Try to use live tool state as last-resort before external navigation
        try {
          const tname = normalizeToolName(tool);
          let liveResult = null;
          if (liveToolStates && Array.isArray(liveToolStates.order)) {
            for (const id of liveToolStates.order) {
              const entry = liveToolStates.map?.[id];
              if (!entry) continue;
              const ename = normalizeToolName(entry.tool_name || '');
              if (ename !== tname) continue;
              const res = entry.tool_result || null;
              if (res) {
                // Prefer matching task id when provided
                const rid = res.task_id || res.overview_result?.task_id || res.metadata?.task_id;
                if (!taskId || (rid && String(rid) === String(taskId))) {
                  liveResult = res;
                }
              }
            }
          }
          if (liveResult && typeof onOpenReport === 'function') {
            onOpenReport({ tool: `functions.${tool}`, tool_result: liveResult }, [], null);
          } else {
            // Fallback: open external in NEW tab to avoid reloading current page
            window.open(buildExternalReportUrl(tool, taskId, excerpt || ''), '_blank');
          }
        } catch (_) {
          // ignore
        }
      }
      const safeExcerpt = (excerpt || '').trim();
      const targetText = safeExcerpt; // strict excerpt matching only
      if (targetText) {
        // Emit a single highlight instruction (no blinking)
        setTimeout(() => {
          try {
            const eventName =
              tool === 'compare_competitors'
                ? 'comp-analysis:highlight'
                : tool === 'reviews_analysis'
                ? 'reviews-analysis:highlight'
                : tool === 'market_analysis_tool'
                ? 'market-analysis:highlight'
                : tool === 'get_who_to_buy_strategy'
                ? 'get-who-to-buy:highlight'
                : 'lp-analysis:highlight';
            const evt = new CustomEvent(eventName, { detail: { text: targetText } });
            window.dispatchEvent(evt);
          } catch (e) {
            /* ignore */
          }
        }, 350);
      }
    } catch {
      // ignore
    }
  };

  // Remark plugin to convert inline trusted report references into link nodes with a special scheme
  const trustedInlinePlugin = () => trustedInlinePluginFactory(normalizeToolName);

  // Special case: inline paywall/insufficient credits card
  const isInsufficientCredits =
    (message && message.subtype === 'insufficient_credits') || (message && message.error && message.error.code === 'insufficient_credits');
  if (isInsufficientCredits) {
    const meta = message.meta || message.error || {};
    const isFreePlan = (() => {
      const flag = meta.is_free_plan;
      if (typeof flag === 'boolean') return flag;
      if (typeof flag === 'string') return flag.trim().toLowerCase() === 'true';
      const plan = String(meta.user_plan || meta.plan || '')
        .trim()
        .toLowerCase();
      return plan === 'free';
    })();
    return isFreePlan ? (
      <InsufficientCreditsFreeCard meta={meta} toolDisplayName={toolDisplayName} />
    ) : (
      <InsufficientCreditsPaidCard meta={meta} toolDisplayName={toolDisplayName} />
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
        mb: 2,
        width: '100%'
      }}
    >
      <Card
        sx={{
          maxWidth: message.type === 'user' ? '85%' : '100%',
          width: message.type === 'user' ? 'auto' : '100%',
          background:
            message.type === 'user'
              ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
              : theme.palette.grey[100],
          color: message.type === 'user' ? theme.palette.primary.contrastText : theme.palette.text.primary,
          borderRadius: message.type === 'user' ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
          boxShadow: `0 2px 10px ${theme.palette.action.hover}`
        }}
      >
        <CardContent sx={{ px: 2, py: message.type === 'user' ? 0.5 : 2, '&:last-child': { pb: message.type === 'user' ? 1 : 2 } }}>
          {/* Top controls removed; controls are rendered in the footer only */}
          {message.type !== 'user' && (nonWebToolCalls.length > 0 || hasWebSearchActivity) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ fontStyle: 'italic', display: 'block', mb: 1 }}>
                🔍 Research tools used:
              </Typography>
              {/* Web search compact counter */}
              {hasWebSearchActivity && (
                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: theme.palette.success.main }} />
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    {`Completed ${webSearchCompletedCount} web search${webSearchCompletedCount === 1 ? '' : 'es'}`}
                  </Typography>
                </Box>
              )}
              {nonWebToolCalls.map((call, idx) => {
                const toolData = getArtifactsForToolCall(call);
                const live = liveEntries.find((e) => call.call_id ? (e.call_id || e.id) === call.call_id : normalizeToolName(e.tool_name || 'unknown_tool') === normalizeToolName(call.tool));
                const loading = !!(live && live.status === 'running' && !toolData.toolResult && !call.tool_result);
                // Prefer history-derived result, then call-provided, then live result as a fallback
                const effectiveToolResult =
                  typeof toolData.toolResult !== 'undefined' && toolData.toolResult !== null
                    ? toolData.toolResult
                    : call.tool_result || (live && typeof live.tool_result !== 'undefined' ? live.tool_result : null);
                return (
                  <Box key={idx} sx={{ mb: 1.25 }}>
                    <ResearchToolBox
                      toolCall={{ ...call, tool_result: effectiveToolResult }}
                      artifacts={toolData.artifacts}
                      onOpenReport={onOpenReport}
                      loading={loading}
                      liveStartTs={live?.startTs}
                      placeCompletedAtBottom={compactLayout}
                      readOnly={readOnly}
                    />
                  </Box>
                );
              })}
            </Box>
          )}
          {/* Market Analysis Tool - Render MarketOverview and MarketTrends inline when fullscreen, View button when minimized */}
          {(() => {
            const marketAnalysisTool = nonWebToolCalls.find((call) => normalizeToolName(call.tool) === 'market_analysis_tool');
            if (marketAnalysisTool) {
              const toolData = getArtifactsForToolCall(marketAnalysisTool);
              const live = liveEntries.find(
                (e) => normalizeToolName(e.tool_name || 'unknown_tool') === normalizeToolName(marketAnalysisTool.tool)
              );
              const loading = !!(live && live.status === 'running' && !toolData.toolResult && !marketAnalysisTool.tool_result);
              const effectiveToolResult =
                typeof toolData.toolResult !== 'undefined' && toolData.toolResult !== null
                  ? toolData.toolResult
                  : marketAnalysisTool.tool_result || (live && typeof live.tool_result !== 'undefined' ? live.tool_result : null);

              if (!loading && effectiveToolResult && effectiveToolResult.market_overview && effectiveToolResult.market_trends) {
                // When minimized, show a View button instead of full components
                if (!isFullscreen) {
                  const handleViewReport = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (onOpenReport && effectiveToolResult) {
                      onOpenReport({ ...marketAnalysisTool, tool_result: effectiveToolResult }, toolData.artifacts || [], null);
                    }
                  };

                  return (
                    <Box sx={{ mb: 1.25 }}>
                      <Card
                        sx={{
                          mb: 0,
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 2,
                          transition: 'all 0.2s ease-in-out',
                          boxShadow: 'none',
                          '&:hover': {
                            boxShadow: `0 4px 12px ${theme.palette.action.hover}`,
                            transform: 'translateY(-1px)'
                          }
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box
                                sx={{
                                  p: 1,
                                  borderRadius: 1,
                                  backgroundColor: `${theme.palette.secondary.main}20`,
                                  color: theme.palette.secondary.main,
                                  display: 'flex',
                                  alignItems: 'center',
                                  mr: 1
                                }}
                              >
                                <AnalyticsIcon />
                              </Box>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                  Market Analysis
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                    Research Tool
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: theme.palette.success.main }} />
                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                      Completed
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<VisibilityIcon />}
                                onClick={handleViewReport}
                                sx={{ height: 28, px: 1.5, fontSize: '0.75rem' }}
                              >
                                View
                              </Button>
                            </Stack>
                          </Box>
                        </CardContent>
                      </Card>
                    </Box>
                  );
                }

                // Only render full components when fullscreen
                if (isFullscreen) {
                  const restrictedCountries = ['IN', 'VN', 'CN', 'BD', 'PK', 'ID'];
                  const trialAllowed = !trialWasActivated && !restrictedCountries.includes(userCountry);
                  const isProUser =
                    plan === 'Trial' || plan === 'Pro' || plan === 'Pro+' || plan === 'Enterprise Pro Max' || plan === 'Enterprise Pro';

                  const handleTrialClick = () => {
                    dispatch(
                      openModal({
                        title: 'Get access to detailed market analysis',
                        subtitle: "Upgrade to Pro to view all market insights and trends. Don't miss out on accessing more data!",
                        trial: trialAllowed
                      })
                    );
                  };

                  return (
                    <Box sx={{ mb: 3, mt: 2 }}>
                      <MarketOverview
                        data={effectiveToolResult.market_overview}
                        forceV2={true}
                        allowAnyRegions={true}
                        isProUser={isProUser}
                        handleTrialClick={handleTrialClick}
                        trialAllowed={trialAllowed}
                      />
                      <MarketTrends
                        marketTrends={{
                          trends: effectiveToolResult.market_trends.trends || [],
                          benchmarks: effectiveToolResult.market_trends.benchmarks || [],
                          industry: effectiveToolResult.market_trends.industry || ''
                        }}
                      />
                    </Box>
                  );
                }
              }
            }
            return null;
          })()}
          {/* Get Who To Buy Strategy Tool - Render inline when fullscreen, View button when minimized */}
          {(() => {
            const getWhoToBuyTool = nonWebToolCalls.find((call) => normalizeToolName(call.tool) === 'get_who_to_buy_strategy');
            if (getWhoToBuyTool) {
              const toolData = getArtifactsForToolCall(getWhoToBuyTool);
              const live = liveEntries.find(
                (e) => normalizeToolName(e.tool_name || 'unknown_tool') === normalizeToolName(getWhoToBuyTool.tool)
              );
              const loading = !!(live && live.status === 'running' && !toolData.toolResult && !getWhoToBuyTool.tool_result);
              let effectiveToolResult =
                typeof toolData.toolResult !== 'undefined' && toolData.toolResult !== null
                  ? toolData.toolResult
                  : getWhoToBuyTool.tool_result || (live && typeof live.tool_result !== 'undefined' ? live.tool_result : null);

              // Parse tool_result if it's a string (JSON wrapped in markdown code blocks)
              effectiveToolResult = parseToolResult(effectiveToolResult);

              if (!loading && effectiveToolResult && (effectiveToolResult.get || effectiveToolResult.client)) {
                // When fullscreen or read-only, always render full component
                // When minimized and not read-only, show a View button instead
                if (!isFullscreen && !readOnly) {
                  const handleViewReport = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (onOpenReport && effectiveToolResult) {
                      onOpenReport({ ...getWhoToBuyTool, tool_result: effectiveToolResult }, toolData.artifacts || [], null);
                    }
                  };

                  return (
                    <Box sx={{ mb: 1.25 }}>
                      <Card
                        sx={{
                          mb: 0,
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 2,
                          transition: 'all 0.2s ease-in-out',
                          boxShadow: 'none',
                          '&:hover': {
                            boxShadow: `0 4px 12px ${theme.palette.action.hover}`,
                            transform: 'translateY(-1px)'
                          }
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box
                                sx={{
                                  p: 1,
                                  borderRadius: 1,
                                  backgroundColor: `${theme.palette.primary.main}20`,
                                  color: theme.palette.primary.main,
                                  display: 'flex',
                                  alignItems: 'center',
                                  mr: 1
                                }}
                              >
                                <CampaignIcon />
                              </Box>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                  Get Who To Buy Strategy
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                    Research Tool
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: theme.palette.success.main }} />
                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                      Completed
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<VisibilityIcon />}
                                onClick={handleViewReport}
                                sx={{ height: 28, px: 1.5, fontSize: '0.75rem' }}
                              >
                                View
                              </Button>
                            </Stack>
                          </Box>
                        </CardContent>
                      </Card>
                    </Box>
                  );
                }

                // Only render full component when fullscreen
                if (isFullscreen) {
                  return (
                    <Box sx={{ mb: 3, mt: 2 }}>
                      <GetWhoToBuyReportViewer reportData={{ analysisData: effectiveToolResult }} />
                    </Box>
                  );
                }
              }
            }
            return null;
          })()}
          {/* Inline reasoning (from server-provided final message fields) */}
          {reasonText ? <ReasoningInline text={reasonText} /> : null}
          <Box
            sx={{
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                mt: 2,
                mb: 1,
                fontWeight: 600,
                '&:first-of-type': {
                  mt: 0
                }
              },
              '& h1': { fontSize: '1.4rem' },
              '& h2': { fontSize: '1.2rem' },
              '& h3': { fontSize: '1.05rem' },
              '& p': {
                mb: 1,
                lineHeight: 1.6,
                fontSize: '0.9rem'
              },
              '& a': {
                color: message.type === 'user' ? theme.palette.primary.contrastText : theme.palette.primary.main,
                textDecoration: 'underline',
                '&:hover': {
                  opacity: 0.8
                }
              },
              '& ul, & ol': {
                pl: 3,
                mb: 1
              },
              '& li': {
                mb: 0.5
              },
              '& code': {
                backgroundColor: message.type === 'user' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.05)',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '0.875rem',
                fontFamily: 'monospace'
              },
              '& pre': {
                backgroundColor: message.type === 'user' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.05)',
                padding: '12px',
                borderRadius: '8px',
                overflow: 'auto',
                mb: 1
              },
              '& pre code': {
                backgroundColor: 'transparent',
                padding: 0
              },
              '& blockquote': {
                borderLeft: `4px solid ${message.type === 'user' ? 'rgba(255,255,255,0.3)' : theme.palette.grey[400]}`,
                pl: 2,
                ml: 0,
                my: 1,
                fontStyle: 'italic',
                opacity: 0.9
              },
              '& strong': {
                fontWeight: 600
              },
              fontSize: '0.9rem'
            }}
          >
            {imageSources.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {imageSources.map((src, idx) => (
                  <Box
                    key={idx}
                    component="img"
                    src={src}
                    alt={`image-${idx}`}
                    sx={{
                      maxWidth: 220,
                      maxHeight: 160,
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      display: 'block',
                      cursor: 'pointer'
                    }}
                    onClick={() => setPreviewSrc(src)}
                  />
                ))}
              </Box>
            ) : showInlineThinking ? (
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                AI is thinking
                <StreamingDots />
              </Typography>
            ) : (
              <MarkdownErrorBoundary content={displayContent}>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, trustedInlinePlugin]}
                  urlTransform={(uri) => uri}
                  components={{
                    a: ({ href, children }) => {
                      const url = String(href || '');
                      if (url.startsWith('trusted:')) {
                        const afterScheme = url.slice('trusted:'.length);
                        const qIndex = afterScheme.indexOf('?');
                        const main = qIndex >= 0 ? afterScheme.slice(0, qIndex) : afterScheme;
                        const query = qIndex >= 0 ? afterScheme.slice(qIndex + 1) : '';
                        const [tool, taskId] = main.split(':');
                        const params = new URLSearchParams(query);
                        const path = params.get('path') || '';
                        const excerpt = params.get('excerpt') || '';

                        return (
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography component="span" variant="caption" sx={{ color: theme.palette.text.secondary }}>
                              {toolDisplayName(tool)}
                            </Typography>
                            <Chip
                              component="span"
                              size="small"
                              label="View"
                              onClick={(e) => {
                                try {
                                  e.preventDefault();
                                  e.stopPropagation();
                                } catch (_) {
                                  /* ignore */
                                }
                                openTrustedReportToPath(tool, taskId, path, excerpt);
                              }}
                            />
                            <IconButton size="small" onClick={() => window.open(buildExternalReportUrl(tool, taskId, excerpt), '_blank')}>
                              <OpenInNewIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        );
                      }
                      return (
                        <a href={url} target="_blank" rel="noopener noreferrer">
                          {children}
                        </a>
                      );
                    }
                  }}
                >
                  {message.streaming
                    ? maskIncompleteGfmTables(stabilizeMarkdownForStreaming(normalizeInlineGfmTables(displayContent)))
                    : normalizeInlineGfmTables(displayContent)}
                </ReactMarkdown>
              </MarkdownErrorBoundary>
            )}
            {/* In-app image preview dialog */}
            <Dialog
              open={!!previewSrc}
              onClose={() => setPreviewSrc(null)}
              maxWidth="lg"
              sx={{ zIndex: (theme) => theme.zIndex.modal + 40 }}
            >
              <Box sx={{ p: 1 }}>
                {previewSrc && (
                  <Box
                    component="img"
                    src={previewSrc}
                    alt="preview"
                    sx={{ maxWidth: '90vw', maxHeight: '80vh', display: 'block', borderRadius: 1 }}
                  />
                )}
              </Box>
            </Dialog>
            {!showInlineThinking && message.streaming && (
              <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
                <StreamingDots />
              </Box>
            )}
            {/* Inline citations already render actionable View links inside the text. */}
          </Box>
          {/* Inline follow-up buttons under assistant messages */}
          {message.type !== 'user' && !message.streaming && Array.isArray(followUpQuestions) && followUpQuestions.length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-start' }}>
              {followUpQuestions.map((q, idx) => (
                <Chip
                  key={idx}
                  size="small"
                  variant="outlined"
                  label={q}
                  onClick={() => onClickFollowUp && onClickFollowUp(q)}
                  sx={(theme) => ({
                    borderRadius: 4,
                    height: 'auto',
                    alignItems: 'flex-start',
                    py: 0.75,
                    px: 1,
                    m: 0.25,
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.divider,
                    transition: 'background-color 0.2s ease, border-color 0.2s ease',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.16),
                      borderColor: alpha(theme.palette.primary.main, 0.15)
                    },
                    '& .MuiChip-label': {
                      whiteSpace: 'normal',
                      display: 'block',
                      textAlign: 'left',
                      lineHeight: 1.35
                    }
                  })}
                />
              ))}
            </Box>
          )}

          {message.status === 'failed' && message.error && message.error !== displayContent && (
            <Typography role="alert" color="error" variant="body2" sx={{ mt: 1 }}>{message.error}</Typography>
          )}
          {/* Footer controls */}
          {message.type !== 'user' && !message.streaming && <FooterControls message={message} content={displayContent} />}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ChatMessage;
